import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, MapPin, User, Hash, ArrowRight, Loader2, ScanLine, Clock } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { PublicNav } from "../../components/PublicNav";
import { captureScanParams, trackScan, PARTY_DOT } from "./tracking";
import { Seo, breadcrumbJsonLd, siteOrigin } from "../../components/Seo";

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC","PR"];
const MODES = [["zip", "ZIP code", Hash], ["name", "Candidate name", User], ["state", "State & office", MapPin]];
const inputCls = "w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-base focus:border-amber-400/60 focus:outline-none placeholder:text-slate-500";

export const RaceResult = ({ r, onOpen }) => (
  <button onClick={() => onOpen(r)} data-testid={`race-result-${r.race_id}`}
    className="w-full text-left rounded-2xl border border-white/10 bg-white/[0.03] hover:border-amber-400/50 hover:bg-white/[0.06] transition-colors p-4 flex items-center gap-4">
    <div className="min-w-0 flex-1">
      <div className="font-display font-bold text-base text-white">{r.label}</div>
      <div className="text-xs text-slate-400 mt-1">{r.n_candidates} declared candidate{r.n_candidates === 1 ? "" : "s"} · live FEC roster</div>
      <div className="flex flex-wrap gap-1.5 mt-2">
        {r.candidates.map((c) => (
          <span key={c.id} className="inline-flex items-center gap-1.5 text-[11px] text-slate-300 bg-white/5 rounded-full px-2 py-0.5">
            <span className={`w-1.5 h-1.5 rounded-full ${PARTY_DOT[c.party] || "bg-slate-400"}`} />{c.name}{c.incumbent ? " · inc." : ""}
          </span>
        ))}
        {r.n_candidates > r.candidates.length && <span className="text-[11px] text-slate-500">+{r.n_candidates - r.candidates.length} more</span>}
      </div>
    </div>
    <span className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-amber-300">See the demo <ArrowRight className="w-4 h-4" /></span>
  </button>
);

export default function ScanLanding() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState("zip");
  const [q, setQ] = useState("");
  const [zip, setZip] = useState("");
  const [state, setState] = useState("");
  const [office, setOffice] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const expired = params.get("expired") === "1";

  useEffect(() => { captureScanParams(params); trackScan(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const search = async (e) => {
    e?.preventDefault();
    setBusy(true); setError(""); setResult(null);
    const p = mode === "zip" ? { zip } : mode === "name" ? { q } : { state, office };
    try { const { data } = await api.get("/public/scan/lookup", { params: p }); setResult(data); }
    catch (err) { setError(errMsg(err, "Search failed — try another way")); }
    finally { setBusy(false); }
  };
  const open = (r) => navigate(`/scan/race/${r.race_id}`);

  return (
    <div className="min-h-screen bg-app text-white" data-testid="scan-landing-page">
      <Seo title="Find your race — free 2026 race demo, no login" description="Search your 2026 U.S. House or Senate race by ZIP, candidate or state. See live candidates, FEC money, poll trends, CTV voter reach and AI marketing plays — then open a no-login demo of TotalReach." path="/scan"
        jsonLd={[{ "@context": "https://schema.org", "@type": "WebPage", name: "Scan. Find your race.", url: `${siteOrigin()}/scan`, potentialAction: { "@type": "SearchAction", target: `${siteOrigin()}/scan?q={search_term_string}`, "query-input": "required name=search_term_string" } }, breadcrumbJsonLd([{ name: "Home", path: "/" }, { name: "Find your race", path: "/scan" }])]} />
      <PublicNav compact />
      <main className="max-w-3xl mx-auto px-5 py-10 md:py-16 space-y-8">
        {expired && (
          <div className="rounded-xl border border-amber-400/30 bg-amber-400/10 px-4 py-3 text-sm text-amber-100 flex items-center gap-2" data-testid="guest-expired-notice">
            <Clock className="w-4 h-4 shrink-0" /> Your 30-minute demo session ended. Find your race again to start another, or book a live walkthrough.
          </div>
        )}
        <div>
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-amber-300 font-medium"><ScanLine className="w-4 h-4" /> Scan · Find your race</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mt-3">See what TotalReach can do <span className="text-amber-300">for your race.</span></h1>
          <p className="text-base text-slate-400 mt-4 max-w-xl">Live candidates and FEC money, poll trends, CTV voter reach and AI-built plays — for the exact race you&apos;re in. No login, no sales call.</p>
        </div>

        <form onSubmit={search} className="rounded-3xl border border-white/10 bg-white/[0.03] p-4 md:p-6 space-y-4" data-testid="scan-search-card">
          <div className="grid grid-cols-3 gap-2" data-testid="scan-mode-tabs">
            {MODES.map(([k, l, Icon]) => (
              <button key={k} type="button" onClick={() => { setMode(k); setResult(null); setError(""); }} data-testid={`scan-mode-${k}`}
                className={`rounded-xl px-3 py-2.5 text-xs md:text-sm font-medium inline-flex items-center justify-center gap-2 border transition-colors ${mode === k ? "border-amber-400/60 bg-amber-400/10 text-amber-200" : "border-white/10 text-slate-400 hover:text-slate-200"}`}>
                <Icon className="w-4 h-4" /> {l}
              </button>
            ))}
          </div>
          {mode === "zip" && <input value={zip} onChange={(e) => setZip(e.target.value.replace(/\D/g, "").slice(0, 5))} inputMode="numeric" placeholder="Your 5-digit ZIP code" autoFocus className={inputCls} data-testid="scan-zip-input" />}
          {mode === "name" && <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Candidate last name (e.g. Cornyn, Ossoff)" autoFocus className={inputCls} data-testid="scan-name-input" />}
          {mode === "state" && (
            <div className="grid grid-cols-2 gap-2">
              <select value={state} onChange={(e) => setState(e.target.value)} className={inputCls} data-testid="scan-state-select">
                <option value="">State…</option>{US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <select value={office} onChange={(e) => setOffice(e.target.value)} className={inputCls} data-testid="scan-office-select">
                <option value="">Senate & House</option><option value="S">U.S. Senate</option><option value="H">U.S. House</option>
              </select>
            </div>
          )}
          <button type="submit" disabled={busy || (mode === "zip" ? zip.length !== 5 : mode === "name" ? !q.trim() : !state)} data-testid="scan-search-btn"
            className="w-full rounded-xl bg-gradient-to-r from-amber-400 to-amber-600 text-on-accent font-bold py-3.5 text-base inline-flex items-center justify-center gap-2 disabled:opacity-50 hover:opacity-90 transition-opacity">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />} Find my race
          </button>
          {error && <div className="text-sm text-rose-300" data-testid="scan-search-error">{error}</div>}
        </form>

        {result && (
          <div className="space-y-3" data-testid="scan-results">
            {result.zip && <div className="text-xs text-slate-400" data-testid="scan-zip-summary">ZIP {result.zip.zip} → {result.zip.districts.map((d) => `${d.state}-${d.district}`).join(", ")} · <a href={result.zip.source_url} target="_blank" rel="noreferrer" className="underline decoration-dotted">Census district map</a></div>}
            {result.races.length === 0 && <div className="rounded-xl border border-dashed border-white/10 p-6 text-center text-sm text-slate-400" data-testid="scan-no-results">No 2026 federal race matched. Try a ZIP code or your state.</div>}
            {result.races.map((r) => <RaceResult key={r.race_id} r={r} onOpen={open} />)}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-500">
          <button onClick={() => navigate("/scan/race/2026-S-TX")} className="text-slate-300 hover:text-amber-300 underline decoration-dotted" data-testid="scan-sample-race-btn">Not in a race? Explore a sample (Texas Senate)</button>
          <button onClick={() => navigate("/request-demo")} className="hover:text-slate-300" data-testid="scan-book-demo-link">Prefer a human? Book a demo</button>
          <button onClick={() => navigate("/directory")} className="hover:text-slate-300">Browse the full directory</button>
        </div>
      </main>
    </div>
  );
}
